import React from 'react';
import { connect } from 'react-redux';
import { map, toPlainObject } from 'lodash';

import {
    DISPLAY_STEP,
    PAYMENT_REQUEST_STATES_PENDING,
    PAYMENT_REQUEST_STATES_VALID,
    PAYMENT_REQUEST_STATES_INVALID,
    TREE_PARITY_EURO_TTC
} from '../../../../constants/payment';
import {
    PAYMENT_REQUEST_TYPE_ASSO,
    PAYMENT_REQUEST_TYPE_IBAN,
    PAYMENT_REQUEST_TYPE_IBAN_TIER,
    PAYMENT_REQUEST_TYPE_NURSERY,
    STATIC_ROOT
} from '../../../../constants';
import { MOMENTJS_DATE_FORMAT_STANDARD } from '../../../../constants/moment';

import { fetchPaymentRequestsIfNeeded } from '../../../../services/account/paymentrequest/ongoing/actions';

import { getValidatedPaidAmount } from '../../../../services/account/paymentrequest/ongoing/selector';

import { toDate } from '../../../../utils/dateUtils';
import { formatAmountCurrency } from '../../../../utils/index';
import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import SvgComponent from '../../../SvgComponent';
import { LoadingComponent } from '../../../loading/LoadingComponent';
import PaymentAccountLegendComponent from './PaymentAccountLegendComponent';

import './PaymentAccount.scss';

const renderPaymentState = (icon, text, width, height, mention) => (
    <React.Fragment>
        <p>
            <span>{text}</span>
            {icon && (
                <span>
                    <SvgComponent
                        src={`${STATIC_ROOT}/icon/picto-${icon}.svg`}
                        width={width}
                        height={height}
                        aria-hidden="true"
                        focusable="false"
                    />
                </span>
            )}
        </p>
        {mention && <p className="mention">{mention}</p>}
    </React.Fragment>
);

class PaymentAccountPage extends React.Component {
    constructor(props) {
        super(props);
        this.state = { index: DISPLAY_STEP };
    }

    componentDidMount() {
        this.props.fetchPaymentRequestsIfNeeded();
        this.scrollTop();
    }

    scrollTop = () => {
        if (document.getElementById('payments')) {
            document.getElementById('payments').scrollIntoView({ behavior: 'smooth' });
        }
    };

    renderConditionnalOnPaymentState(paymentState, paymentDate, paymentType) {
        const { i18n } = this.props;

        if (PAYMENT_REQUEST_STATES_PENDING.includes(paymentState)) {
            const mention =
                [PAYMENT_REQUEST_TYPE_IBAN, PAYMENT_REQUEST_TYPE_IBAN_TIER].includes(paymentType) &&
                i18n.WAITING_CCP_MENTION;

            return renderPaymentState('pending', i18n.STATES.WAITING, 20, 20, mention);
        } else if (PAYMENT_REQUEST_STATES_VALID.includes(paymentState) && paymentDate >= 0) {
            return renderPaymentState('check', i18n.STATES.PAID, 20, 20);
        } else if (PAYMENT_REQUEST_STATES_INVALID.includes(paymentState)) {
            return renderPaymentState(null, i18n.STATES.INVALID, 20, 20);
        } else {
            return renderPaymentState('pending', i18n.STATES.WAITING, 20, 20);
        }
    }

    renderPaymentType(paymentType, calculatedAmount) {
        const { i18n } = this.props;

        switch (paymentType) {
            case PAYMENT_REQUEST_TYPE_ASSO:
                return i18n.TYPES.ASSOCIATION;
            case PAYMENT_REQUEST_TYPE_IBAN_TIER:
                return i18n.TYPES.PAYMENT_IBAN_TIER;
            case PAYMENT_REQUEST_TYPE_IBAN:
                return i18n.TYPES.PAYMENT_IBAN;
            case PAYMENT_REQUEST_TYPE_NURSERY:
                return convertHtmlToReactDomWithParams(
                    i18n.TYPES.TREE,
                    calculatedAmount,
                    calculatedAmount > 1 ? 's' : ''
                );
            default:
                return null;
        }
    }

    calculateTreeAmount = (paymentType, paidAmount) => {
        return paymentType === PAYMENT_REQUEST_TYPE_NURSERY
            ? paidAmount / TREE_PARITY_EURO_TTC
            : paidAmount;
    };

    renderSeeMoreButton() {
        const { i18n, data } = this.props;
        const { index } = this.state;
        const length = Array.isArray(data) ? data.length : 0;

        if (length >= DISPLAY_STEP && index < length) {
            return (
                <div className="payment-button">
                    <button
                        type="button"
                        id="more-payment-button"
                        className="see-more-button"
                        title={i18n.SEE_MORE_OPERATION}
                        onClick={this.handleSeeMoreClick}>
                        <span className="plus" aria-hidden="true">
                            +
                        </span>
                        <span className="text">{i18n.SEE_MORE_OPERATION}</span>
                    </button>
                </div>
            );
        }
    }

    handleSeeMoreClick = () => {
        this.setState({
            index: this.state.index + DISPLAY_STEP
        });
    };

    render() {
        const { data, fetching, i18n, paymentRequestTotalAmount, user } = this.props;
        const { index } = this.state;

        const safeData = Array.isArray(data) ? data : [];
        const sortedData = safeData
            .filter(p => p.type)
            .sort((p1, p2) => p2.date - p1.date)
            .slice(0, index);

        return (
            <div id="payments">
                <div className="total" role="status" aria-live="polite">
                    {convertHtmlToReactDomWithParams(
                        i18n.JACKPOT,
                        toDate(user.creationDate, MOMENTJS_DATE_FORMAT_STANDARD),
                        formatAmountCurrency(paymentRequestTotalAmount)
                    )}
                </div>

                <div className="information-div" role="note">
                    <span aria-hidden="true">
                        <SvgComponent
                            src={`${STATIC_ROOT}/information.svg`}
                            width="20"
                            height="20"
                            aria-hidden="true"
                            focusable="false"
                        />
                    </span>
                    <span>{convertHtmlToReactDomWithParams(i18n.DESCRIPTION)}</span>
                </div>

                {!fetching && sortedData.length > 0 && (
                    <table className="payments-table" aria-label={i18n.TABLE_LABEL}>
                        <thead className="sr-only">
                            <tr>
                                <th scope="col">{i18n.COLUMN_DATE}</th>
                                <th scope="col">{i18n.COLUMN_TYPE}</th>
                                <th scope="col">{i18n.COLUMN_AMOUNT}</th>
                                <th scope="col">{i18n.COLUMN_STATE}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {map(sortedData, (payment, i) => {
                                const calculatedAmount = this.calculateTreeAmount(
                                    payment.type,
                                    payment.paidAmount
                                );
                                return (
                                    <tr className="container-payment" key={i}>
                                        <td>
                                            {toDate(payment.date, MOMENTJS_DATE_FORMAT_STANDARD)}
                                        </td>
                                        <td>
                                            {this.renderPaymentType(payment.type, calculatedAmount)}
                                        </td>
                                        <td>{formatAmountCurrency(payment.paidAmount)}</td>
                                        <td>
                                            {this.renderConditionnalOnPaymentState(
                                                payment.state,
                                                payment.date,
                                                payment.type
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {(fetching || safeData.length === 0) && (
                    <div
                        className={`container-payment empty ${fetching ? 'fetching' : ''}`}
                        role="status"
                        aria-live="polite">
                        {fetching ? <LoadingComponent /> : i18n.NO_PAYMENT}
                    </div>
                )}

                {this.renderSeeMoreButton()}

                <PaymentAccountLegendComponent />
            </div>
        );
    }
}

function mapStateToProps(state) {
    const user = toPlainObject(state.account.user);

    return {
        i18n: state.i18n.page.ACCOUNT.PAYMENTS,
        paymentRequestTotalAmount: getValidatedPaidAmount(state),
        data: state.account.paymentrequest.ongoing.requests || [],
        fetching: state.account.paymentrequest.ongoing.fetching,
        user: user
    };
}

const mapDispatchToProps = {
    fetchPaymentRequestsIfNeeded
};

export default connect(mapStateToProps, mapDispatchToProps)(PaymentAccountPage);



// WEBPACK FOOTER //
// ./src/components/pages/account/payment/PaymentAccountPage.js